import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@proaxiv/ui'
import type { AppEvent } from '../store/eventsSlice'
import type { ParticipantRole } from '../store/registrationsSlice'
import { Button } from './Button'
import { ROLE_STYLES } from './roleStyles'
import { RecipientLanguagePicker } from './RecipientLanguagePicker'
import { formatEmail, isValidEmail } from '../utils/email'
import { normaliseLanguage, type ReportLanguage } from '../utils/downloadParticipantReport'

// "Staff" is hidden from the frontend picker (backend/data model still support it for any
// existing participants) — only these three roles can be newly assigned via the UI.
const REGISTER_ROLES: ParticipantRole[] = ['Audience', 'Speaker', 'Host']

/** Form data submitted by the admin manual-registration modal. */
export interface AdminRegisterFormData {
  firstName: string
  lastName?: string
  email: string
  companyName: string
  consentMediaRecording: boolean
  role: ParticipantRole
  /** Email language for this participant, chosen by the organiser. */
  language: string
}

interface Props {
  event: AppEvent
  onClose: () => void
  /** When provided, called instead of dispatching to Redux. */
  onSubmit?: (data: AdminRegisterFormData) => Promise<void>
}

const inputCls =
  'w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition-colors placeholder:opacity-40'

/**
 * Modal for the organizer to register a participant on their behalf.
 * Per BPMN issue #51, this is always available regardless of registration status.
 * The registration is automatically accepted (registeredByOrganizer = true).
 */
export function BackupRegisterModal({ onClose, onSubmit }: Props) {
  const { t, i18n } = useTranslation()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [company, setCompany] = useState('')
  const [role, setRole] = useState<ParticipantRole>('Audience')
  const [language, setLanguage] = useState<ReportLanguage>(() => normaliseLanguage(i18n.language))
  const [submitting, setSubmitting] = useState(false)

  const emailValid = isValidEmail(email)
  const showEmailError = emailTouched && email.length > 0 && !emailValid
  const canSubmit = firstName.trim().length > 0 && emailValid && company.trim().length > 0

  async function handleSubmit() {
    if (!canSubmit || submitting) return
    setSubmitting(true)
    try {
      if (onSubmit) {
        await onSubmit({
          firstName: firstName.trim(),
          lastName: lastName.trim() || undefined,
          email: formatEmail(email),
          companyName: company.trim(),
          consentMediaRecording: true,
          role,
          language,
        })
      }
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal labelledBy="backup-reg-title">
      <div className="border-pax-purple/50 flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border bg-white shadow-2xl ring-1 ring-black/5">
        {/* Header */}
        <div className="border-pax-purple/20 flex items-center justify-between border-b px-6 py-4">
          <h2 id="backup-reg-title" className="text-pax-black text-lg font-bold">
            {t('reg_backup_modal_title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-pax-gray hover:text-pax-black cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-pax-gray mb-5 text-sm">{t('reg_backup_modal_subtitle')}</p>

          <div className="flex flex-col gap-4">
            {/* Last + First name */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="br-last" className="text-pax-black text-xs font-medium">
                  {t('reg_field_last_name')}
                </label>
                <input
                  id="br-last"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  className={`${inputCls} border-pax-purple/30 text-pax-black focus:border-pax-blue bg-white`}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="br-first" className="text-pax-black text-xs font-medium">
                  {t('reg_field_first_name')} <span className="text-pax-red">*</span>
                </label>
                <input
                  id="br-first"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jane"
                  className={`${inputCls} border-pax-purple/30 text-pax-black focus:border-pax-blue bg-white`}
                />
              </div>
            </div>

            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="br-email" className="text-pax-black text-xs font-medium">
                {t('reg_field_email')} <span className="text-pax-red">*</span>
              </label>
              <input
                id="br-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                placeholder="participant@example.com"
                aria-invalid={showEmailError}
                className={`${inputCls} ${showEmailError ? 'border-red-400 focus:border-red-400' : 'border-pax-purple/30 focus:border-pax-blue'} text-pax-black bg-white`}
              />
              {showEmailError && (
                <p className="text-xs font-medium text-red-400">
                  {t('invite_modal_email_invalid')}
                </p>
              )}
            </div>

            {/* Company */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="br-company" className="text-pax-black text-xs font-medium">
                {t('reg_field_company')} <span className="text-pax-red">*</span>
              </label>
              <input
                id="br-company"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Acme Corp"
                className={`${inputCls} border-pax-purple/30 text-pax-black focus:border-pax-blue bg-white`}
              />
            </div>

            {/* Role */}
            <div className="flex flex-col gap-1.5">
              <span className="text-pax-black text-xs font-medium">{t('accept_role_label')}</span>
              {/* Badge-styled options (matching the RoleBadge/RoleSelect pill look used elsewhere)
                  instead of a native <select>, so the picker reads the same way as every other
                  role control in the app. */}
              <div
                role="radiogroup"
                aria-label={t('accept_role_label')}
                className="border-pax-purple/30 flex flex-wrap gap-2 rounded-xl border bg-white p-2"
              >
                {REGISTER_ROLES.map((r) => (
                  <Button
                    key={r}
                    type="button"
                    variant="unstyled"
                    role="radio"
                    aria-checked={role === r}
                    onClick={() => setRole(r)}
                    className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-[10px] font-semibold tracking-wide uppercase transition-all ${ROLE_STYLES[r]} ${
                      role === r
                        ? 'ring-pax-blue ring-2 ring-offset-1'
                        : 'opacity-50 hover:opacity-100'
                    }`}
                  >
                    {t(`role_${r.toLowerCase()}`)}
                  </Button>
                ))}
              </div>
            </div>

            <RecipientLanguagePicker
              id="br-lang"
              value={language}
              onChange={setLanguage}
              hint={t('reg_backup_language_hint')}
            />

            {/* Compliance disclaimer — GDPR and media consent are implied by clicking Register */}
            <p className="bg-pax-orange-warm/8 border-pax-orange-warm/30 text-pax-black rounded-xl border p-3 text-sm leading-relaxed">
              {t('reg_consent_on_behalf')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-pax-purple/20 flex items-center justify-end gap-3 border-t px-6 py-4">
          <Button variant="secondary" onClick={onClose}>
            {t('btn_cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || submitting}
          >
            {t('reg_backup_confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
